import { TramoVesUsdt, TramosVesUsdtResponse } from '../../services/wallet-ves-client.service';
import { montoEnUsdtParaTramo } from 'src/app/shared/utils/monto-usdt-tramo';

/**
 * Tramos de VES→USDT (21/10/2026). Funciones puras, sin Angular, para que el
 * cálculo se pruebe solo y la pantalla nunca dependa de que la llamada de
 * tramos responda: ante cualquier falta de datos se cobra la base, como antes.
 *
 * Mismas reglas que el backend (`tramos-usdt.calculo.ts` en la API):
 *   - el tramo se elige por los USDT equivalentes a la BASE (montoVes ÷ base);
 *     si el GET trae `precioCorte` válido (23/10), con `montoVes ÷ precioCorte`
 *     (el PROM P2P), como el servidor. La tasa y el ahorro siguen contra la base;
 *   - es el más alto cuyo «desde» no supera ese monto; bajo el primero, el primero.
 * El backend igual recalcula los USDT y acepta la tasa con 1 % de tolerancia.
 */

export interface TasaTramo {
  /** Tasa a cobrar (VES por USDT). Sin tramos, la base. */
  tasa: number;
  /** Null cuando no hay tramos o la llamada falló. */
  tramo: TramoVesUsdt | null;
  /** 'A', 'B', 'C'… por posición. Vacío si no hay tramo. */
  letra: string;
  /** VES que se ahorra frente a pagar los mismos USDT a la base. */
  ahorroVes: number;
  /** El ahorro llevado a USDT a la base (aproximado, solo para mostrar). */
  ahorroUsdt: number;
}

/** Diferencia de tasa (VES) desde la que se avisa "La tasa ha cambiado". */
export const TOLERANCIA_TASA = 0.005;
/** Fallos seguidos de la llamada de tramos desde los que se cae a la base. */
export const MAX_FALLOS_TRAMOS = 3;
/** Edad máxima de los tramos guardados antes de caer a la base. */
export const VIGENCIA_TRAMOS_MS = 5 * 60_000;

/** Resultado de pedir los tramos: distingue "falló" de "llegó vacío". */
export type LecturaTramos =
  | { ok: true; data: TramosVesUsdtResponse | null }
  | { ok: false };

/** Lo último bueno de tramos y cuántas veces seguidas falló la llamada. */
export interface EstadoTramos {
  data: TramosVesUsdtResponse | null;
  /** Instante de la última lectura buena. Null si nunca hubo. */
  actualizadoEn: number | null;
  fallosSeguidos: number;
}

export const ESTADO_TRAMOS_INICIAL: EstadoTramos = { data: null, actualizadoEn: null, fallosSeguidos: 0 };

/**
 * Suma una lectura al estado. Una lectura buena (aunque venga vacía) reemplaza
 * los datos; una fallida CONSERVA los últimos buenos y solo cuenta el fallo.
 */
export const registrarLectura = (estado: EstadoTramos, lectura: LecturaTramos, ahora: number): EstadoTramos =>
  lectura.ok
    ? { data: lectura.data, actualizadoEn: ahora, fallosSeguidos: 0 }
    : { ...estado, fallosSeguidos: estado.fallosSeguidos + 1 };

/**
 * Los tramos ya no se pueden confiar: 3 fallos seguidos o datos de hace más
 * de 5 minutos. Entonces se cobra la base como "tasa estimada" y se avisa.
 */
export const tramosDesactualizados = (estado: EstadoTramos, ahora: number): boolean => {
  if (estado.fallosSeguidos >= MAX_FALLOS_TRAMOS) return true;
  if (estado.fallosSeguidos === 0 || estado.actualizadoEn == null) return false;
  return ahora - estado.actualizadoEn > VIGENCIA_TRAMOS_MS;
};

/** Datos con los que se cotiza: los guardados, o null (la base) si están desactualizados. */
export const tramosParaCotizar = (estado: EstadoTramos, ahora: number): TramosVesUsdtResponse | null =>
  tramosDesactualizados(estado, ahora) ? null : estado.data;

/**
 * ¿Se avisa "La tasa ha cambiado"? Solo si las dos llamadas (tasa y tramos)
 * respondieron bien y la tasa para el MISMO monto empeoró para el cliente más
 * que la tolerancia (más VES por USDT). Si mejoró se actualiza sin aviso.
 */
export const avisarCambioDeTasa = (
  anterior: number,
  nueva: number,
  ambasOk: boolean,
  tolerancia = TOLERANCIA_TASA,
): boolean => ambasOk && anterior > 0 && nueva - anterior > tolerancia;

/**
 * La tarjeta "Compra más, paga menos" solo tiene sentido con 2 tramos o más
 * y si no todos cobran lo mismo.
 */
export const hayDescuentoPorTramo = (tramos: TramoVesUsdt[]): boolean => {
  if (!tramos || tramos.length < 2) return false;
  const primera = Number(tramos[0].tasa);
  return tramos.some((t) => Math.abs(Number(t.tasa) - primera) > TOLERANCIA_TASA);
};

const round2 = (n: number): number => Math.round(n * 100) / 100;
const floor2 = (n: number): number => Math.floor(n * 100) / 100;

/** Letra del tramo por su posición: 0 → 'A'. */
export const letraTramo = (indice: number): string =>
  indice >= 0 ? String.fromCharCode(65 + indice) : '';

/** Tramos usables: con tasa válida y ordenados por «desde». */
export const tramosValidos = (data: TramosVesUsdtResponse | null | undefined): TramoVesUsdt[] => {
  const lista = Array.isArray(data?.tramos) ? data!.tramos : [];
  return lista
    .filter((t) => t && Number.isFinite(Number(t.tasa)) && Number(t.tasa) > 0 && Number.isFinite(Number(t.desde)))
    .sort((a, b) => Number(a.desde) - Number(b.desde));
};

/**
 * Tramo de un monto en USDT: el más alto cuyo «desde» no lo supera. Bajo el
 * primero, el primero. Null si no hay tramos.
 */
export const tramoDelMonto = <T extends { desde: number }>(tramos: T[], montoUsdtBase: number): T | null => {
  if (!tramos?.length) return null;
  let elegido = tramos[0];
  for (const t of tramos) if (montoUsdtBase >= Number(t.desde)) elegido = t;
  return elegido;
};

/** Base con la que se eligen los tramos: la del endpoint de tramos, si viene; si no, la de la pantalla. */
const baseDe = (data: TramosVesUsdtResponse | null | undefined, tasaBase: number): number => {
  const b = Number(data?.base);
  return Number.isFinite(b) && b > 0 ? b : tasaBase;
};

const sinTramo = (tasaBase: number): TasaTramo => ({
  tasa: tasaBase,
  tramo: null,
  letra: '',
  ahorroVes: 0,
  ahorroUsdt: 0,
});

/** Arma el resultado con el ahorro frente a la base para los mismos USDT. */
const conTramo = (tramos: TramoVesUsdt[], tramo: TramoVesUsdt, base: number, montoUsdt: number): TasaTramo => {
  const tasa = Number(tramo.tasa);
  const ahorroVes = tasa < base && montoUsdt > 0 ? round2(montoUsdt * (base - tasa)) : 0;
  return {
    tasa,
    tramo,
    letra: letraTramo(tramos.indexOf(tramo)),
    ahorroVes,
    ahorroUsdt: ahorroVes > 0 ? floor2(ahorroVes / base) : 0,
  };
};

/**
 * Tasa para un monto en VES (el cliente escribe los bolívares). `tasaBase` es
 * la `tasaFinal` de hoy: si no hay tramos o algo falla, se devuelve tal cual.
 */
export const tasaParaMontoVes = (
  data: TramosVesUsdtResponse | null | undefined,
  montoVes: number,
  tasaBase: number,
): TasaTramo => {
  try {
    const tramos = tramosValidos(data);
    if (!tramos.length) return sinTramo(tasaBase);
    const base = baseDe(data, tasaBase);
    const ves = Number(montoVes) || 0;
    const tramo = tramoDelMonto(tramos, montoEnUsdtParaTramo(ves, data?.precioCorte, base));
    if (!tramo) return sinTramo(tasaBase);
    const montoUsdt = floor2(ves / Number(tramo.tasa));
    return conTramo(tramos, tramo, base, montoUsdt);
  } catch {
    return sinTramo(tasaBase);
  }
};

/**
 * Inverso: el cliente escribe los USDT. El tramo se toma por ese monto y los
 * VES salen de `round2(usdt × tasa)`. Como el backend elige por montoVes ÷ base
 * (que da un poco menos que los USDT cuando el tramo mejora la base), se
 * confirma que esos VES caigan en el mismo tramo; si caen en uno más chico se
 * usa ese, para que la tasa informada sea la que el backend va a cobrar por
 * esos VES (es la que después compara el chequeo previo al envío).
 */
export const tasaParaMontoUsdt = (
  data: TramosVesUsdtResponse | null | undefined,
  montoUsdt: number,
  tasaBase: number,
): TasaTramo & { montoVes: number } => {
  const usdt = Number(montoUsdt) || 0;
  try {
    const tramos = tramosValidos(data);
    if (!tramos.length) return { ...sinTramo(tasaBase), montoVes: round2(usdt * tasaBase) };
    const base = baseDe(data, tasaBase);
    let tramo = tramoDelMonto(tramos, usdt);
    if (!tramo) return { ...sinTramo(tasaBase), montoVes: round2(usdt * tasaBase) };
    let montoVes = round2(usdt * Number(tramo.tasa));
    // Solo se baja de tramo (a lo sumo N vueltas). Si al bajar los VES vuelven
    // a caer en el de arriba (franja angosta junto al corte), se quedan esos
    // VES y se informa el tramo que el backend va a cobrar: el cliente recibe
    // un poco más de lo que escribió, nunca menos.
    for (let i = 0; i < tramos.length; i++) {
      const segunBackend = tramoDelMonto(tramos, montoEnUsdtParaTramo(montoVes, data?.precioCorte, base));
      if (!segunBackend || segunBackend === tramo) break;
      if (tramos.indexOf(segunBackend) > tramos.indexOf(tramo)) {
        tramo = segunBackend;
        break;
      }
      tramo = segunBackend;
      montoVes = round2(usdt * Number(tramo.tasa));
    }
    return { ...conTramo(tramos, tramo, base, usdt), montoVes };
  } catch {
    return { ...sinTramo(tasaBase), montoVes: round2(usdt * tasaBase) };
  }
};

/** Rótulo del rango de un tramo en la tarjeta "Compra más, paga menos". */
export const rotuloTramo = (
  tramos: TramoVesUsdt[],
  indice: number,
  fmt: (n: number) => string,
): string => {
  const t = tramos[indice];
  if (!t) return '';
  if (indice === 0) return `Hasta ${fmt(Number(t.hasta))} USDT`;
  // «Desde N» y no «Más de N−1»: el back elige el tramo con `monto >= desde`,
  // así que un monto entre N−1 y N (p. ej. 5.000,5 con desde 5.001) sigue en
  // el tramo anterior. «Más de 5.000» lo hacía parecer del último.
  if (indice === tramos.length - 1) return `Desde ${fmt(Number(t.desde))} USDT`;
  return `${fmt(Number(t.desde))} a ${fmt(Number(t.hasta))} USDT`;
};

/** Desde cuánto aplica un tramo, para la nota de ahorro ("desde 5.001"). Mismo corte que el back (`>= desde`). */
export const umbralNotaTramo = (
  tramos: TramoVesUsdt[],
  tramo: TramoVesUsdt | null,
  fmt: (n: number) => string,
): string => {
  if (!tramo) return '';
  return `desde ${fmt(Number(tramo.desde))}`;
};
